import { useState, useCallback } from 'react'
import { useSettingsStore } from '../stores/settingsStore'
import { useGeneratorModel } from '../features/generators/components/GeneratorModelSelector'
import { withGeneratorModel } from '../lib/generatorModelSelection'

// JSONクリーニング関数（エネミージェネレータと同じ強固な対策）
function cleanJsonResponse(text: string): string {
  let cleanedJson = text
  
  // 複数のコードブロック形式に対応
  // 1. ```json ... ``` 形式
  if (cleanedJson.includes('```')) {
    const codeBlockMatch = cleanedJson.match(/```(?:json)?\s*([\s\S]*?)```/)
    if (codeBlockMatch) {
      cleanedJson = codeBlockMatch[1]
    } else {
      // コードブロックが不完全な場合（```で始まるが終わらない）
      cleanedJson = cleanedJson.replace(/^```(?:json)?\s*/, '')
    }
  }
  
  // 2. JSONオブジェクトまたは配列を抽出（最初の{または[から最後の}または]まで）
  const objectStart = cleanedJson.indexOf('{')
  const arrayStart = cleanedJson.indexOf('[')
  
  if (objectStart !== -1 || arrayStart !== -1) {
    const start = objectStart === -1 ? arrayStart : 
                  arrayStart === -1 ? objectStart : 
                  Math.min(objectStart, arrayStart)
    
    if (start !== -1) {
      cleanedJson = cleanedJson.substring(start)
      
      // 対応する終端を探す
      const isArray = cleanedJson[0] === '['
      const endChar = isArray ? ']' : '}'
      const lastEnd = cleanedJson.lastIndexOf(endChar)
      
      if (lastEnd !== -1) {
        cleanedJson = cleanedJson.substring(0, lastEnd + 1)
      }
    }
  }
  
  // 強固なクリーニング処理
  cleanedJson = cleanedJson
    // まずダブルエスケープされた改行を単一エスケープに戻す
    .replace(/\\\\n/g, '\\n')
    // 実際の改行文字を\\nに置換（文字列リテラル内のみ）
    .replace(/"([^"\\]*(\\.[^"\\]*)*)"/g, (match) => {
      return match.replace(/\n/g, '\\n').replace(/\r/g, '\\r')
    })
    // 制御文字を除去（タブ、改行、キャリッジリターンを除く）
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    // 末尾のカンマを除去
    .replace(/,(\s*[}\]])/g, '$1')
    // 不正なエスケープシーケンスを修正
    .replace(/([^\\])\\([^\\nrt"'\/bfux])/g, '$1\\\\$2')
  
  // 不完全なJSONの修復を試みる
  cleanedJson = repairIncompleteJson(cleanedJson)
  
  return cleanedJson
}

// 不完全なJSONを修復する関数（エネミーバッチ対応強化版）
function repairIncompleteJson(json: string): string {
  console.log('[JSON Repair] Starting repair, input length:', json.length)
  
  // 最後の有効な完全なオブジェクトまたは配列の終端を見つける
  const lastValidObjectEnd = json.lastIndexOf('}')
  const lastValidArrayEnd = json.lastIndexOf(']')
  const lastValidEnd = Math.max(lastValidObjectEnd, lastValidArrayEnd)
  
  if (lastValidEnd > 0) {
    // 最後の有効な終端以降に開始記号がないか確認
    const afterEnd = json.substring(lastValidEnd + 1)
    if (!afterEnd.includes('{') && !afterEnd.includes('[')) {
      console.log('[JSON Repair] Found clean end at position:', lastValidEnd)
      return json.substring(0, lastValidEnd + 1)
    }
  }
  
  // 開いている文字列を閉じる
  const openQuoteMatch = json.match(/"[^"]*$/)
  if (openQuoteMatch) {
    console.log('[JSON Repair] Closing open string')
    json = json + '"'
  }
  
  // 未完了のプロパティを処理（値が途中で切れている場合）
  const incompleteProperty = json.match(/,\s*"[^"]+"\s*:\s*"?[^"\],}]*$/)
  if (incompleteProperty) {
    console.log('[JSON Repair] Found incomplete property, adding null')
    json = json.replace(/,\s*"[^"]+"\s*:\s*"?[^"\],}]*$/, '')
  }
  
  // 開いているブラケットとブレースの数を数える
  let openBraces = 0
  let openBrackets = 0
  let inString = false
  let escapeNext = false
  
  for (let i = 0; i < json.length; i++) {
    const char = json[i]
    
    if (escapeNext) {
      escapeNext = false
      continue
    }
    
    if (char === '\\') {
      escapeNext = true
      continue
    }
    
    if (char === '"') {
      inString = !inString
      continue
    }
    
    if (!inString) {
      if (char === '{') openBraces++
      if (char === '}') openBraces--
      if (char === '[') openBrackets++
      if (char === ']') openBrackets--
    }
  }
  
  console.log(`[JSON Repair] Open braces: ${openBraces}, Open brackets: ${openBrackets}`)
  
  // 必要な閉じ括弧を追加
  while (openBraces > 0) {
    json += '}'
    openBraces--
  }
  
  while (openBrackets > 0) {
    json += ']'
    openBrackets--
  }
  
  console.log('[JSON Repair] Repair complete, output length:', json.length)
  return json
}

function parseJsonSafely<T>(text: string): T {
  // 空文字列チェック
  if (!text || text.trim() === '') {
    console.warn('[parseJsonSafely] Empty text received')
    throw new Error('空のレスポンスです')
  }
  
  const cleanedJson = cleanJsonResponse(text)
  
  try {
    return JSON.parse(cleanedJson)
  } catch (firstError) {
    // バッチモード（配列）の場合、最初に部分救済を試みる
    if (cleanedJson.trim().startsWith('[')) {
      console.log('[parseJsonSafely] Attempting partial recovery for batch mode')
      const partialResult = tryParsePartialArray(cleanedJson)
      if (partialResult && partialResult.length > 0) {
        console.warn(`Partial batch result recovered: ${partialResult.length} items`)
        return partialResult as T
      }
    }
    
    // より積極的なクリーニングを試みる
    const moreCleanedJson = cleanedJson
      .replace(/,(\s*[}\]])/g, '$1')
      .replace(/([^\\])\\([^\\nrt"'\/bfux])/g, '$1\\\\$2')
    
    try {
      return JSON.parse(moreCleanedJson)
    } catch (secondError) {
      // 再度部分救済を試みる（クリーニング後）
      if (moreCleanedJson.trim().startsWith('[')) {
        const partialResult = tryParsePartialArray(moreCleanedJson)
        if (partialResult && partialResult.length > 0) {
          console.warn(`Partial batch result recovered after cleaning: ${partialResult.length} items`)
          return partialResult as T
        }
      }
      
      console.error('JSON parse failed after cleaning:', secondError)
      console.error('First 500 chars of cleaned JSON:', cleanedJson.substring(0, 500))
      
      // 最後の手段として、エラーでも空配列を返す（バッチモードの場合）
      if (cleanedJson.trim().startsWith('[')) {
        console.error('Returning empty array due to parse failure')
        return [] as unknown as T
      }
      
      throw new Error(`JSONパースエラー: ${secondError.message}`)
    }
  }
}

// 部分的な配列を救出する関数（エネミーバッチ対応強化版）
function tryParsePartialArray(json: string): any[] | null {
  try {
    console.log('[Partial Array Recovery] Starting recovery from', json.length, 'chars')
    
    const objects: any[] = []
    
    // まず配列の開始を見つける
    const arrayStart = json.indexOf('[')
    if (arrayStart === -1) {
      console.warn('[Partial Array Recovery] No array found')
      return null
    }
    
    // 配列部分を取得
    const arrayContent = json.substring(arrayStart)
    
    // より賢い正規表現でオブジェクトを抽出
    // basicInfo, appearance, abilities などの主要フィールドを持つオブジェクトを探す
    let depth = 0
    let start = -1
    let inString = false
    let escapeNext = false
    let currentObject = ''
    
    for (let i = 0; i < arrayContent.length; i++) {
      const char = arrayContent[i]
      
      if (escapeNext) {
        escapeNext = false
        if (depth > 0) currentObject += char
        continue
      }
      
      if (char === '\\' && !escapeNext) {
        escapeNext = true
        if (depth > 0) currentObject += char
        continue
      }
      
      if (char === '"' && !escapeNext) {
        inString = !inString
        if (depth > 0) currentObject += char
        continue
      }
      
      if (!inString) {
        if (char === '{') {
          if (depth === 0) {
            start = i
            currentObject = char
          } else {
            currentObject += char
          }
          depth++
        } else if (char === '}') {
          depth--
          currentObject += char
          
          if (depth === 0 && start !== -1) {
            // オブジェクトが完成した
            try {
              const obj = JSON.parse(currentObject)
              if (obj && typeof obj === 'object') {
                // エネミーまたはアイテムの最小要件をチェック
                const hasBasicInfo = obj.basicInfo && (obj.basicInfo.name || obj.basicInfo.title)
                const hasName = obj.name || obj.title
                
                if (hasBasicInfo || hasName) {
                  objects.push(obj)
                  const identifier = obj.basicInfo?.name || obj.basicInfo?.title || obj.name || obj.title
                  console.log(`[Partial Array Recovery] Recovered complete object: ${identifier}`)
                }
              }
            } catch (e) {
              // パース失敗、次を試す
            }
            start = -1
            currentObject = ''
          }
        } else if (depth > 0) {
          currentObject += char
        }
      } else if (depth > 0) {
        currentObject += char
      }
    }
    
    // 未完了のオブジェクトがある場合、修復を試みる
    if (depth > 0 && currentObject.length > 0) {
      console.log('[Partial Array Recovery] Attempting to repair incomplete object')
      
      // 不完全な文字列リテラルを閉じる
      let fixedObject = currentObject
      
      // 開いている文字列を検出して閉じる
      const openQuote = fixedObject.match(/"[^"]*$/)
      if (openQuote) {
        fixedObject += '"'
      }
      
      // null値で未完了のプロパティを補完
      const lastComma = fixedObject.lastIndexOf(',')
      const lastColon = fixedObject.lastIndexOf(':')
      if (lastColon > lastComma) {
        // プロパティ値が未入力
        if (!fixedObject.trim().endsWith('"')) {
          fixedObject += 'null'
        }
      }
      
      // 必要な閉じ括弧を追加
      let tempDepth = 0
      let tempInString = false
      let tempEscape = false
      
      for (const char of fixedObject) {
        if (tempEscape) {
          tempEscape = false
          continue
        }
        if (char === '\\') {
          tempEscape = true
          continue
        }
        if (char === '"' && !tempEscape) {
          tempInString = !tempInString
          continue
        }
        if (!tempInString) {
          if (char === '{' || char === '[') tempDepth++
          if (char === '}' || char === ']') tempDepth--
        }
      }
      
      while (tempDepth > 0) {
        fixedObject += '}'
        tempDepth--
      }
      
      try {
        const obj = JSON.parse(fixedObject)
        if (obj && typeof obj === 'object') {
          const hasBasicInfo = obj.basicInfo && (obj.basicInfo.name || obj.basicInfo.title)
          const hasName = obj.name || obj.title
          
          if (hasBasicInfo || hasName) {
            objects.push(obj)
            const identifier = obj.basicInfo?.name || obj.basicInfo?.title || obj.name || obj.title
            console.log(`[Partial Array Recovery] Recovered repaired object: ${identifier}`)
          }
        }
      } catch (e) {
        console.warn('[Partial Array Recovery] Could not repair last object:', e)
      }
    }
    
    console.log(`[Partial Array Recovery] Total recovered: ${objects.length} objects`)
    return objects.length > 0 ? objects : null
  } catch (e) {
    console.error('[Partial Array Recovery] Fatal error:', e)
    return null
  }
}

interface StreamOptions {
  onChunk?: (chunk: string) => void
  onProgress?: (accumulated: number) => void
  onComplete?: (result: any) => void
  onError?: (error: Error) => void
}

export function useStreamGenerator(type: string) {
  const [isStreaming, setIsStreaming] = useState(false)
  const [progress, setProgress] = useState(0)
  const [partialContent, setPartialContent] = useState('')
  const settingsStore = useSettingsStore()
  const modelId = useGeneratorModel(type)
  
  const generateStream = useCallback(async (
    params: any,
    options: StreamOptions = {}
  ) => {
    const apiKey = settingsStore.getActiveApiKeyForProvider?.(
      settingsStore.selectedProvider
    )?.key ?? settingsStore.activeApiKey?.key
    if (!apiKey) {
      throw new Error('APIキーが設定されていません')
    }
    
    setIsStreaming(true)
    setProgress(0)
    setPartialContent('')
    
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/stream/${type}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': apiKey
          },
          body: JSON.stringify(withGeneratorModel(params, modelId))
        }
      )
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const reader = response.body?.getReader()
      const decoder = new TextDecoder()
      
      if (!reader) {
        throw new Error('No reader available')
      }
      
      let buffer = ''
      
      while (true) {
        const { done, value } = await reader.read()
        
        if (done) break
        
        buffer += decoder.decode(value, { stream: true })
        
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') {
              setIsStreaming(false)
              setProgress(100)
              return
            }
            
            try {
              const parsed = JSON.parse(data)
              
              switch (parsed.type) {
                case 'chunk':
                  setPartialContent(prev => prev + parsed.content)
                  const estimatedProgress = Math.min(90, (parsed.accumulated / 10000) * 100)
                  setProgress(estimatedProgress)
                  options.onProgress?.(parsed.accumulated)
                  options.onChunk?.(parsed.content)
                  break
                  
                case 'complete':
                  setProgress(100)
                  console.log('[StreamGenerator] Received complete event with result:', parsed.result)
                  options.onComplete?.(parsed.result)
                  break
                  
                case 'parse_error':
                  console.warn('[StreamGenerator] Parse error from server, attempting client-side parse:', parsed.error)
                  console.log('[StreamGenerator] Raw content length:', parsed.raw?.length || 0)
                  
                  // 空レスポンスの場合（Gemini-2.5-proの負荷時によくある）
                  if (!parsed.raw || parsed.raw.length === 0) {
                    console.warn('[StreamGenerator] Empty response received, likely due to Gemini API overload')
                    options.onError?.(new Error('Gemini APIから空のレスポンスが返されました。混雑している可能性があります。'))
                    break
                  }
                  
                  // クライアント側で再パースを試みる
                  try {
                    const result = parseJsonSafely(parsed.raw)
                    console.log('[StreamGenerator] Client-side parse result:', result)
                    if (result) {
                      options.onComplete?.(result)
                    } else {
                      // 結果が空の場合もエラーではなく空配列を返す
                      console.warn('Parse resulted in empty result, returning empty array')
                      options.onComplete?.([])
                    }
                  } catch (clientParseError) {
                    console.error('Client-side parse also failed:', clientParseError)
                    
                    // バッチモードの場合は部分救済を試みる
                    if (parsed.raw && parsed.raw.trim().startsWith('[')) {
                      const partialResult = tryParsePartialArray(parsed.raw)
                      if (partialResult && partialResult.length > 0) {
                        console.warn(`Recovered ${partialResult.length} items from failed parse`)
                        options.onComplete?.(partialResult)
                        break
                      }
                    }
                    
                    // バッチモードの場合は空配列、それ以外は生データを返す
                    if (parsed.raw && parsed.raw.trim().startsWith('[')) {
                      options.onComplete?.([])
                    } else {
                      options.onComplete?.({ raw: parsed.raw })
                    }
                  }
                  break
                  
                case 'error':
                  throw new Error(parsed.message)
              }
            } catch (e) {
              console.error('Parse error:', e)
              if (e instanceof Error && e.message) {
                options.onError?.(e)
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Stream error:', error)
      setIsStreaming(false)
      
      if (error instanceof Error) {
        if (error.message.includes('429')) {
          options.onError?.(new Error('APIキーのレート制限に達しました。しばらく待ってから再度お試しください。'))
        } else {
          options.onError?.(error)
        }
      }
    } finally {
      setIsStreaming(false)
    }
  }, [type, settingsStore, modelId])
  
  return {
    generateStream,
    isStreaming,
    progress,
    partialContent
  }
}
